import {
  AdditiveBlending,
  BoxGeometry,
  Color,
  DynamicDrawUsage,
  Group,
  InstancedMesh,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  type Texture,
} from 'three'
import { Rng } from '@/core/Rng'
import type { ArenaDef, ArenaState } from '@/game/arenas/Arena'

const TILE = 1.5
const TILE_THICKNESS = 0.85
const GRAVITY = 16
/**
 * How far in from the boundary a slab still counts as "edge".
 *
 * Deliberately narrower than one slab. At 1.1 m every tile of a five-metre-wide
 * bridge qualified, and the whole walkway lit up as if it were the rim — which
 * is exactly backwards, since on a narrow platform the player most needs to see
 * where the *middle* is.
 */
const EDGE_BAND = 0.7

interface TileState {
  x: number
  z: number
  /** Signed SDF distance at full arena size; negative means inside. */
  dist: number
  baseY: number
  /** Where this slab sits when the platform is whole. */
  restY: number
  /** Its permanent random yaw, so a reset restores the same jumble. */
  yaw: number
  falling: boolean
  vy: number
  spinX: number
  spinZ: number
  rotX: number
  rotZ: number
  delay: number
}

/**
 * The platform: a grid of independent slabs plus a glowing edge marker.
 *
 * Tiling rather than extruding the footprint is what makes the collapse
 * possible — each slab outside the shrinking area simply detaches and tumbles.
 *
 * The edge marker is the single most important readability feature in the game.
 * On a fixed overhead camera over a dark arena, players could not tell where the
 * ground ended, which in a game about pushing people off ground is fatal. It is
 * built from instanced quads laid on top of the outermost slabs, so it follows
 * a circle, a chamfered slab, a cross and a ring with a hole in it without any
 * per-shape code — and it re-forms correctly as the platform shrinks.
 */
export class ArenaMesh {
  readonly group = new Group()

  private readonly tiles: TileState[] = []
  private readonly mesh: InstancedMesh
  private readonly edgeMesh: InstancedMesh
  private readonly dummy = new Object3D()
  private readonly baseColors: Color[] = []
  private readonly scratch = new Color()
  private readonly edgeTint = new Color()
  private readonly edgeColor: Color
  private readonly warnColor = new Color(0xff3a1a)

  private warning = 0
  private lastEdgeScale = -1
  private lastWarnStep = -1

  constructor(def: ArenaDef, concrete: Texture, roughness: Texture) {
    const rng = new Rng(0xbeef)
    this.edgeColor = new Color(def.lighting.edgeColor)

    const geometry = new BoxGeometry(TILE * 0.98, TILE_THICKNESS, TILE * 0.98)
    const material = new MeshStandardMaterial({
      color: def.surface.tint,
      map: concrete,
      roughnessMap: roughness,
      // Almost fully rough and non-metallic: any specular response at all on a
      // surface this large turns the key light into a broad sheen across the
      // floor, which reads as glare rather than as concrete.
      roughness: 0.99,
      metalness: 0.01,
    })

    const cols = Math.ceil((def.extentX * 2) / TILE) + 2
    const rows = Math.ceil((def.extentZ * 2) / TILE) + 2
    const startX = def.centerX - ((cols - 1) * TILE) / 2
    const startZ = def.centerZ - ((rows - 1) * TILE) / 2

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = startX + c * TILE
        const z = startZ + r * TILE
        // Keep a slab if its centre is inside, so the rim looks chewed rather
        // than laser-cut — appropriate for broken concrete.
        const dist = def.shape.distance(x, z)
        if (dist > TILE * 0.12) continue
        const restY = -TILE_THICKNESS / 2 + rng.spread(0.035)
        this.tiles.push({
          x,
          z,
          dist,
          baseY: restY,
          restY,
          yaw: rng.spread(0.03),
          falling: false,
          vy: 0,
          spinX: rng.spread(2.6),
          spinZ: rng.spread(2.6),
          rotX: 0,
          rotZ: 0,
          delay: 0,
        })
      }
    }

    this.mesh = new InstancedMesh(geometry, material, this.tiles.length)
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage)
    this.mesh.receiveShadow = true
    // Emphatically NOT a shadow caster. Each slab is a solid box, so with the
    // key light low in the sky every slab shadowed its neighbour and the whole
    // floor rendered as one flat dark mass — the single biggest reason the
    // arena was invisible. It still *receives* shadows from props and fighters.
    this.mesh.castShadow = false

    const tint = new Color(def.surface.tint)
    for (let i = 0; i < this.tiles.length; i++) {
      const tile = this.tiles[i]
      this.dummy.position.set(tile.x, tile.baseY, tile.z)
      this.dummy.rotation.set(0, tile.yaw, 0)
      this.dummy.updateMatrix()
      this.mesh.setMatrixAt(i, this.dummy.matrix)

      const c = tint.clone().multiplyScalar(1 + rng.spread(0.12))
      this.baseColors.push(c)
      this.mesh.setColorAt(i, c)
    }
    this.mesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
    this.group.add(this.mesh)

    // Emissive edge caps, drawn over the outermost slabs. Additive and
    // unaffected by tone mapping so bloom picks them up as a hot outline.
    const edgeGeometry = new PlaneGeometry(TILE * 0.98, TILE * 0.98)
    edgeGeometry.rotateX(-Math.PI / 2)
    this.edgeMesh = new InstancedMesh(
      edgeGeometry,
      new MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.22,
        blending: AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
      this.tiles.length,
    )
    this.edgeMesh.instanceMatrix.setUsage(DynamicDrawUsage)
    this.edgeMesh.count = 0
    this.edgeMesh.frustumCulled = false
    this.group.add(this.edgeMesh)

    // The void: a far, near-black floor that fog swallows long before it reads
    // as a surface, giving the platform something to float above.
    const abyss = new Mesh(
      new PlaneGeometry(400, 400),
      new MeshBasicMaterial({ color: def.lighting.background, fog: true }),
    )
    abyss.rotation.x = -Math.PI / 2
    abyss.position.y = -55
    this.group.add(abyss)

    this.rebuildEdge(0)
  }

  /**
   * Restores every fallen slab for a new round.
   *
   * Cheaper and safer than rebuilding the mesh: a rebuild allocated a fresh
   * instanced geometry, material and 400×400 void plane every round while the
   * old ones stayed resident on the GPU, and it re-ran the camera fit solve.
   */
  reset(): void {
    for (let i = 0; i < this.tiles.length; i++) {
      const tile = this.tiles[i]
      tile.falling = false
      tile.vy = 0
      tile.rotX = 0
      tile.rotZ = 0
      tile.delay = 0
      tile.baseY = tile.restY

      this.dummy.position.set(tile.x, tile.baseY, tile.z)
      this.dummy.rotation.set(0, tile.yaw, 0)
      this.dummy.updateMatrix()
      this.mesh.setMatrixAt(i, this.dummy.matrix)
      this.mesh.setColorAt(i, this.baseColors[i])
    }
    this.mesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true

    this.warning = 0
    this.lastEdgeScale = -1
    this.lastWarnStep = -1
    this.rebuildEdge(0)
  }

  update(arena: ArenaState, dt: number): void {
    let dirty = false

    for (let i = 0; i < this.tiles.length; i++) {
      const tile = this.tiles[i]

      if (!tile.falling) {
        if (arena.contains(tile.x, tile.z)) continue
        tile.falling = true
        // Staggering the drop turns a uniform ring collapse into a crumble.
        tile.delay = ((i * 37) % 17) / 34
        dirty = true
      }

      if (tile.delay > 0) {
        tile.delay -= dt
        continue
      }

      tile.vy -= GRAVITY * dt
      tile.baseY += tile.vy * dt
      tile.rotX += tile.spinX * dt
      tile.rotZ += tile.spinZ * dt
      dirty = true

      this.dummy.position.set(tile.x, tile.baseY, tile.z)
      this.dummy.rotation.set(tile.rotX, 0, tile.rotZ)
      this.dummy.updateMatrix()
      this.mesh.setMatrixAt(i, this.dummy.matrix)
    }

    if (dirty) this.mesh.instanceMatrix.needsUpdate = true

    // Warning glow ramps up before the collapse, then holds a low pulse.
    const target = arena.warning ? 1 : arena.collapsing ? 0.35 : 0
    this.warning += (target - this.warning) * Math.min(1, dt * 5)

    // The edge only needs rebuilding when the boundary actually moved, or when
    // the warning colour has drifted enough to be visible.
    const warnStep = Math.round(this.warning * 12)
    if (
      Math.abs(arena.scale - this.lastEdgeScale) > 0.004 ||
      warnStep !== this.lastWarnStep
    ) {
      this.rebuildEdge(this.warning, arena)
      this.lastEdgeScale = arena.scale
      this.lastWarnStep = warnStep
    }

    const edgeMat = this.edgeMesh.material as MeshBasicMaterial
    const pulse = this.warning > 0.02 ? 0.72 + 0.28 * Math.sin(performance.now() * 0.009) : 1
    edgeMat.opacity = (0.2 + this.warning * 0.4) * pulse
  }

  /**
   * Places an emissive cap on every slab within `EDGE_BAND` of the boundary and
   * warms the slab colours behind them.
   */
  private rebuildEdge(warning: number, arena?: ArenaState): void {
    // A separate colour, not `scratch`. Aliasing the two made the slab tinting
    // below a no-op — `lerp(x, x, t)` — so the diffuse warming of the danger
    // zone never actually happened while still uploading every frame.
    const color = this.edgeTint.copy(this.edgeColor).lerp(this.warnColor, warning)
    ;(this.edgeMesh.material as MeshBasicMaterial).color.copy(color)

    let count = 0
    const tintDirty = this.mesh.instanceColor !== null

    for (let i = 0; i < this.tiles.length; i++) {
      const tile = this.tiles[i]
      if (tile.falling) continue

      // Without an ArenaState (the very first build) the platform is full size.
      const distance = arena ? -arena.distance(tile.x, tile.z) : -tile.dist
      if (distance < 0) continue

      const edge = distance < EDGE_BAND ? 1 - distance / EDGE_BAND : 0
      if (edge > 0.02) {
        this.dummy.position.set(tile.x, tile.baseY + TILE_THICKNESS / 2 + 0.012, tile.z)
        this.dummy.rotation.set(0, 0, 0)
        this.dummy.scale.setScalar(1)
        this.dummy.updateMatrix()
        this.edgeMesh.setMatrixAt(count, this.dummy.matrix)
        count++
      }

      if (tintDirty) {
        // Slabs near the drop are also lifted in diffuse, so the danger zone
        // reads even with bloom disabled — gently, as a hint of warmth.
        this.scratch.copy(this.baseColors[i]).lerp(color, edge * 0.22)
        this.mesh.setColorAt(i, this.scratch)
      }
    }

    this.edgeMesh.count = count
    this.edgeMesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
  }

  dispose(): void {
    this.mesh.geometry.dispose()
    ;(this.mesh.material as MeshStandardMaterial).dispose()
    this.edgeMesh.geometry.dispose()
    ;(this.edgeMesh.material as MeshBasicMaterial).dispose()
  }
}
